import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./header.module.scss";
import { connect } from "react-redux";
import {
    setActiveFeature,
    setSearchBarVisible,
    setActiveFilter,
    setActivePage,
    setMapType,
    setBasemap,
    initActiveFilters,
} from "../../actions/ui";
import {
    selectSearchBarVisible,
    selectActiveFilters,
    selectActivePage,
    selectActiveFeature,
    selectAllFundData,
    selectActiveProject,
    selectViewingFund,
    selectIsInternal,
    selectMapType,
    selectBasemap,
} from "../../reducers/";
import Search from "../search";
import { projectShape } from "../../modules/project";
import geojsonConfig from "../../modules/geojsonConfig";
import { CloseButton } from "../ui/close-button/CloseButton";
import ZoomNswButton from "../ui/map-buttons/ZoomNswButton";

const Header = ({
    fundData,
    viewingFund,
    activeFilters,
    setActiveFilter,
    basemap,
    mapType,
    activeFeature,
    nameSelector,
    searchBarVisible,
    setSearchBarVisible,
    activePage,
    setActivePage,
    activeProject,
    setActiveFeature,
    setBasemap,
    isInternal,
    setMapType,
    initActiveFilters,
    isMobile,
    appHeight,
}) => {
    const [filterBoxOpen, setFilterBoxOpen] = useState(false);
    const [mapTypeBoxOpen, setMapTypeBoxOpen] = useState(false);

    const funds = Object.keys(fundData);

    // Save all possible filters to state
    useEffect(() => {
        initActiveFilters(funds);
    }, [funds]);

    const handleToggleFilterClick = fund => {
        return e => {
            return setActiveFilter(fund);
        };
    };
    const isActiveFilter = fund => {
        if (activeFilters.length === 0) {
            // if no filters are clicked, then they are all lit up
            return true;
        }
        if (!activeFilters.includes(fund)) {
            // If filters are clicked but this is enabled, light up
            return true;
        }
        // We are filtering and this one isn't active
        return false;
    };

    // Whether the button will 'grow' when hovered
    // This indicates it will be selected when pressed
    // This happens when it's deselected,
    // OR, all filters are off.
    const shouldGrow = fund => {
        return activeFilters.length === 0 || activeFilters.includes(fund);
    };

    const renderFilter = () => {
        if (viewingFund) return null;
        if (!filterBoxOpen) {
            return (
                <button
                    className={`${styles.headerButton} ${styles.filterButton}`}
                    onClick={() => setFilterBoxOpen(true)}
                    data-filtering={activeFilters.length > 0}
                    aria-label={`Filter projects by funding source`}
                    tabIndex={1}
                >
                    {!isMobile && <span>Filter</span>}
                </button>
            );
        }
        const filterItems = funds.map(fund => {
            return (
                <div key={fund} className={styles.filterItem}>
                    <button
                        data-active={isActiveFilter(fund)}
                        data-grow={shouldGrow(fund)}
                        className={styles.filterItemButton}
                        onClick={handleToggleFilterClick(fund)}
                        tabIndex={1}
                    >
                        <img src={fundData[fund].icon} alt={fund} />
                    </button>
                    <label
                        onClick={handleToggleFilterClick(fund)}
                        data-active={isActiveFilter(fund)}
                    >
                        {fundData[fund].displayName}
                    </label>
                </div>
            );
        });

        return (
            <div className={styles.filters} style={{ maxHeight: appHeight - 32 }}>
                <div className={styles.filtersHeader}>
                    <span>Filter by fund</span>
                    <CloseButton
                        onClick={() => setFilterBoxOpen(false)}
                        ariaLabel={"Close filters dropdown."}
                    />
                </div>
                {filterItems}
                <button
                    className={styles.resetFiltersButton}
                    onClick={() => {
                        setActiveFilter(null);
                    }}
                    tabIndex={1}
                >
                    Reset filters
                </button>
            </div>
        );
    };

    const renderBasemapToggle = () => {
        const buttonText = basemap === "ROADMAP" ? "Satellite" : "Roads";
        return (
            <button
                onClick={() => {
                    const newBasemap = basemap === "ROADMAP" ? "HYBRID" : "ROADMAP";
                    setBasemap(newBasemap);
                }}
                className={styles.headerButton}
                aria-label={`Toggle base map to ${buttonText}`}
                tabIndex={1}
            >
                {buttonText}
            </button>
        );
    };

    const renderPageToggle = () => {
        return (
            <button
                onClick={() => {
                    const newPage = activePage === "list" ? "map" : "list";
                    setActivePage(newPage);
                }}
                data-page={activePage}
                className={`${styles.headerButton} ${styles.toggleButton}`}
                aria-label={`Go to ${activePage === "list" ? "map" : "list"} view`}
                tabIndex={1}
            >
                {!isMobile && (
                    <span style={{ marginRight: 4 }}>
                        {activePage === "list" ? (
                            <span>Map View</span>
                        ) : (
                            <span>List View</span>
                        )}
                    </span>
                )}
            </button>
        );
    };

    const renderMapType = () => {
        if (!isInternal) return;
        if (!mapTypeBoxOpen) {
            return (
                <button
                    className={`${styles.headerButton} ${styles.filterButton}`}
                    onClick={() => setMapTypeBoxOpen(true)}
                >
                    <span>Map Type</span>
                </button>
            );
        }
        const mapTypeItems = Object.keys(geojsonConfig).map(mType => {
            const name = geojsonConfig[mType].displayName;
            return (
                <div key={mType} className={styles.filterItem}>
                    <button
                        onClick={() => setMapType(mType)}
                        data-active={mapType === mType}
                        disabled={mapType === mType}
                        className={styles.mapTypeButton}
                    >
                        {name}
                    </button>
                </div>
            );
        });
        return (
            <div className={styles.filters}>
                <div className={styles.filtersHeader}>
                    <span>Map Type</span>
                    <button
                        onClick={() => setMapTypeBoxOpen(false)}
                        className={styles.filterCloseButton}
                    >
                        &times;
                    </button>
                </div>
                {mapTypeItems}
            </div>
        );
    };

    const renderActiveFeatureName = isMobile => {
        if (activeFeature) {
            return (
                <p className={styles.activeFeatureName} tabIndex={1}>
                    {/* Very little room on mobile */}
                    {!isMobile && <span>LGA: </span>}
                    <span>{nameSelector(activeFeature)}</span>
                </p>
            );
        }
        if (activePage === "map") return null;
        return (
            <p
                className={styles.activeFeatureName}
                tabIndex={1}
                aria-label={"All New South Wales projects showing"}
            >
                NSW
            </p>
        );
    };

    const renderContent = () => {
        if (activeProject) {
            return null;
        }
        if (searchBarVisible) {
            return (
                <div className={styles.search}>
                    <Search />
                </div>
            );
        }

        return (
            <div className={styles.headerWrapper}>
                <div className={styles.left}>
                    <button
                        className={`${styles.searchButton}`}
                        style={{
                            width: isMobile ? 34 : "auto",
                            padding: isMobile ? 0 : `0px 10px`,
                        }}
                        onClick={() => setSearchBarVisible(true)}
                        aria-label={"Search for projects or locations."}
                        tabIndex={1}
                    >
                        <img
                            src={`${process.env.PUBLIC_URL}/img/search-icon-white.svg`}
                            alt="search for projects or locations"
                        />
                        {!isMobile && <span>Search</span>}
                    </button>

                    {renderActiveFeatureName(isMobile)}
                </div>
                <div className={styles.right}>
                    {!isMobile && renderBasemapToggle()}
                    {!isMobile && (
                        <ZoomNswButton
                            onClick={() => {
                                setActiveFeature(null);
                            }}
                            ariaLabel={
                                activePage === "list"
                                    ? "Show all New South Wales projects"
                                    : "Zoom to New South Wales"
                            }
                            activePage={activePage}
                        />
                    )}
                    {renderMapType()}
                    {renderFilter()}
                    {renderPageToggle()}
                </div>
            </div>
        );
    };

    return <div className={styles.header}>{renderContent()}</div>;
};

Header.propTypes = {
    nameSelector: PropTypes.func.isRequired,
    setActiveFilter: PropTypes.func.isRequired,
    setActiveFeature: PropTypes.func.isRequired,
    setSearchBarVisible: PropTypes.func.isRequired,
    setActivePage: PropTypes.func.isRequired,
    activePage: PropTypes.string.isRequired,
    searchBarVisible: PropTypes.bool.isRequired,
    activeFilters: PropTypes.arrayOf(PropTypes.string),
    fundData: PropTypes.object.isRequired,
    activeFeature: PropTypes.object,
    activeProject: PropTypes.shape(projectShape),
    viewingFund: PropTypes.string,
    isInternal: PropTypes.bool.isRequired,
    mapType: PropTypes.string.isRequired,
    basemap: PropTypes.string.isRequired,
    setMapType: PropTypes.func.isRequired,
    setBasemap: PropTypes.func.isRequired,
    initActiveFilters: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
    appHeight: PropTypes.number.isRequired,
};

const mapStateToProps = s => ({
    searchBarVisible: selectSearchBarVisible(s),
    activeFilters: selectActiveFilters(s),
    activePage: selectActivePage(s),
    activeFeature: selectActiveFeature(s),
    activeProject: selectActiveProject(s),
    fundData: selectAllFundData(s),
    viewingFund: selectViewingFund(s),
    isInternal: selectIsInternal(s),
    mapType: selectMapType(s),
    basemap: selectBasemap(s),
});
const mapDispatchToProps = {
    setActiveFeature,
    setSearchBarVisible,
    setActiveFilter,
    setActivePage,
    setMapType,
    setBasemap,
    initActiveFilters,
};
export default connect(mapStateToProps, mapDispatchToProps)(Header);



// WEBPACK FOOTER //
// ./src/components/header/header.js